import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function FormShell({ title, subtitle, children }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="form-back">
        <ArrowLeft className="h-4 w-4" /> Back to Dashboard
      </button>
      <Card className="border-slate-200 shadow-none">
        <div className="px-5 sm:px-6 py-5 border-b border-slate-100">
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-5 sm:p-6 space-y-5">{children}</div>
      </Card>
    </div>
  );
}

export function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-slate-700 font-medium">
        {label}{required && <span className="text-red-500"> *</span>}
      </Label>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function CurrencyInput({ value, onChange, testId, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
      <Input type="number" inputMode="decimal" className="pl-7" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

export function AutocompleteInput({ value, onChange, options = [], placeholder, testId }) {
  const listId = `${testId}-list`;
  const uniq = [...new Set(options.filter(Boolean))];
  return (
    <>
      <Input list={listId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} autoComplete="off" />
      <datalist id={listId}>
        {uniq.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

export function MultiCheck({ items, selected, onChange, getLabel, getSub, testIdPrefix, empty }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
      {items.length === 0 ? (
        <div className="p-3 text-sm text-slate-400">{empty || "No items available"}</div>
      ) : items.map((it) => (
        <label key={it.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`${testIdPrefix}-${it.code || it.id}`}>
          <input type="checkbox" className="h-4 w-4 accent-blue-600 shrink-0" checked={selected.includes(it.id)} onChange={() => toggle(it.id)} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-800 truncate">{getLabel(it)}</div>
            {getSub && <div className="text-xs text-slate-400">{getSub(it)}</div>}
          </div>
        </label>
      ))}
    </div>
  );
}
